import _ from "lodash";
import { type UnknownAction } from "@reduxjs/toolkit";
import {
  CreatePodcastFailed,
  CreatePodcastRequest,
  CreatePodcastResponse,
  DeletePodcastErrorResponse,
  DeletePodcastResponse,
  Podcast,
  PodcastValidationError,
  PublishPodcastErrorResponse,
  PublishPodcastResponse,
  UpdatePodcastErrorResponse,
  UpdatePodcastPropertyRequest,
  UpdatePodcastRequest,
  UpdatePodcastResponse,
  AddAssetToPodcastRequest,
  CreatePodcastEditorChatRequest,
  PodcastEditorSessionState,
  CreatePodcastEditorChatResponse,
  CreatePodcastEditorChatErrorResponse,
  QuickGeneratePodcastRequest,
  PodcastTranscriptionWebsocketResponse,
  GenerateTranscriptForPodcastRequest,
  FetchPodcastResponse,
  PodcastAudioWebsocketResponse,
  UpdateValidationErrorsForPodcastRequest,
  FetchPublicPodcastRequest,
  FetchPublicPodcastResponse,
  FetchPublicPodcastErrorResponse,
  VoicePreset,
  FetchVoicePresetResponse,
  FetchVoicePresetErrorResponse,
  PodcastEditorChatState,
  FetchPodcastTranscriptionForEditChat,
  FetchPodcastTranscriptionForEditChatResponse,
  FetchPodcastTranscriptionForEditChatErrorResponse,
  GenerateAudioForPodcastRequest,
  PodcastTopicsWebsocketResponse
} from "src/types/coaching/podcast";

import {
  CREATE_PODCAST_REQUEST,
  CREATING_PODCAST_SUCCEEDED,
  CREATING_PODCAST_FAILED,
  FETCH_DRAFT_PODCAST_REQUEST,
  FETCHING_DRAFT_PODCAST_SUCCEEDED,
  FETCHING_DRAFT_PODCAST_FAILED,
  EDIT_PODCAST_REQUEST,
  UPDATE_PODCAST_PROPERTY_REQUEST,
  INVALIDATE_PODCAST_CREATION_STATE,
  UPDATE_PODCAST_REQUEST,
  UPDATING_PODCAST_SUCCEEDED,
  UPDATING_PODCAST_FAILED,
  INVALIDATE_PODCAST_UPDATE_STATE,
  PUBLISHING_PODCAST_FAILED,
  PUBLISHING_PODCAST_SUCCEEDED,
  PUBLISH_PODCAST_REQUEST,
  INVALIDATE_PUBLISH_PODCAST,
  DELETE_PODCAST_REQUEST,
  DELETING_PODCAST_SUCCEEDED,
  DELETING_PODCAST_FAILED,
  DISCARD_PODCAST_DRAFT_REQUEST,
  DISCARDING_PODCAST_DRAFT_SUCCEEDED,
  DISCARDING_PODCAST_DRAFT_FAILED,
  ADD_ASSET_TO_PODCAST,
  CREATING_PODCAST_EDITOR_CHAT_REQUEST,
  CREATING_PODCAST_EDITOR_CHAT_SUCCEEDED,
  CREATING_PODCAST_EDITOR_CHAT_FAILED,
  FETCH_PODCAST_TRANSCRIPTION_REQUEST,
  FETCHING_PODCAST_TTRANSCRIPTION_SUCCEEDED,
  FETCHING_PODCAST_TRANSCRIPTION_FAILED,
  QUICK_GENERATE_PODCAST_REQUEST,
  WS_PODCAST_TRANSCRIPT_GENERATION_COMPLETED,
  GENERATE_TRANSCRIPT_FOR_PODCAST_REQUEST,
  GENERATING_TRANSCRIPT_FOR_PODCAST_SUCCEEDED,
  GENERATING_TRANSCRIPT_FOR_PODCAST_FAILED,
  QUICK_GENERATING_PODCAST_SUCCEEDED,
  QUICK_GENERATING_PODCAST_FAILED,
  SYNCING_PODCAST_PROCESSING_STATE_SUCCEEDED,
  WS_PODCAST_AUDIO_GENERATION_COMPLETED,
  INVALIDATE_QUICK_GENERATING_PODCAST_STATE,
  UPDATE_VALIDATION_ERRORS_FOR_PODCAST,
  FETCH_PUBLIC_PODCAST_REQUEST,
  FETCHING_PUBLIC_PODCAST_SUCCEEDED,
  FETCHING_PUBLIC_PODCAST_FAILED,
  ENROLL_PODCAST_REQUEST,
  ENROLLING_PODCAST_SUCCEEDED,
  ENROLLING_PODCAST_FAILED,
  FETCH_PODCAST_VOICE_PRESETS_REQUEST,
  FETCHING_PODCAST_VOICE_PRESETS_SUCCEEDED,
  FETCHING_PODCAST_VOICE_PRESETS_FAILED,
  GENERATE_AUDIO_FOR_PODCAST_REQUEST,
  GENERATING_AUDIO_FOR_PODCAST_SUCCEEDED,
  GENERATING_AUDIO_FOR_PODCAST_FAILED,
  WS_PODCAST_EDIT_TRANSCRIPT_COMPLETED,
  FETCH_PODCAST_TRANSCRIPTION_FOR_EDIT_CHAT_REQUEST,
  FETCHING_PODCAST_TRANSCRIPTION_FOR_EDIT_CHAT_SUCCEEDED,
  FETCHING_PODCAST_TRANSCRIPTION_FOR_EDIT_CHAT_FAILED,
  INITIATE_PODCAST_EDITOR_REQUEST,
  INVALIDATE_PODCAST_EDITOR_REQUEST,
  WS_PODCAST_TOPICS_GENERATION_COMPLETED,
  INVALIDATE_DRAFT_PODCAST,
} from "src/redux/actions/actionTypes";
import {
  DraftCourseSummary,
  PublishedCourseSummary,
} from "src/types/coaching/courses";
import { Asset, PreviewAsset } from "src/types/assets";
import { validatePodcastRequest } from "src/pages/coaching/manage/podcasts/edit/helper";
import { EnrollCourseErrorResponse, EnrollCourseResponse } from "src/types/coaching/learners";

interface PodcastsState {
  drafts: DraftPodcastItemsState;
  published: PublishedPodcastItemsState;
  loading: boolean;
  items: Podcast[];
  error: any;
  pagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
    hasMoreItems: boolean;
  };
  creation: PodcastCreationState | null;
  edit: PodcastEditState | null;
  update: {
    [key: string]: PodcastUpdateState;
  };
  publish: PodcastPublishState | null;
  deletion: {
    [key: string]: PodcastDeletionState;
  };
  podcast: {
    [key: string]: PodcastState;
  };
  voicePresets: {
    [key: string]: PodcastVoicePresetsState;
  };
}

interface DraftPodcastItemsState {
  loading: boolean;
  items: DraftCourseSummary[];
  error: any;
  pagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
    hasMoreItems: boolean;
  };
}

interface PublishedPodcastItemsState {
  loading: boolean;
  items: PublishedCourseSummary[];
  error: any;
  pagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
    hasMoreItems: boolean;
  };
}

interface PodcastCreationState {
  creating: boolean;
  created: boolean;
  item: Podcast | null;
  error: any;
}

interface AssetState {
  loading: boolean;
  item: Asset | null;
  preview: {
    loading: boolean;
    item: PreviewAsset | null;
    error: any;
  };
  error: any;
}

interface PodcastEditState {
  item: Podcast;
  errors: PodcastValidationError[];
  assets: {
    [key: string]: AssetState;
  };
  editorSession: PodcastEditorSessionState | null;
  quickGenerate?: PodcastQuickGenerateState | null;
  generateTranscript?: PodcastTranscriptGenerateState | null;
  generateAudio?: PodcastAudioGenerateState | null;
}

interface PodcastQuickGenerateState {
  generating: boolean;
  generated: boolean;
  error: any;
}

interface PodcastTranscriptGenerateState {
  generating: boolean;
  generated: boolean;
  error: any;
}

interface PodcastAudioGenerateState {
  generating: boolean;
  generated: boolean;
  error: any;
}

interface PodcastUpdateState {
  id: string;
  updating: boolean;
  updated: boolean;
  item: Podcast | null;
  error: any;
}

interface PodcastPublishState {
  id: string;
  publishing: boolean;
  published: boolean;
  item: Podcast | null;
  error: any;
}

interface PodcastDeletionState {
  id: string;
  deleting: boolean;
  deleted: boolean;
  error: any;
}

interface EnrollPodcastState {
  id?: string,
  enrolling: boolean,
  enrolled: boolean,
  error: any
  parentCourse?: string;
}

interface PodcastState {
  loading: boolean;
  loaded: boolean;
  item: Podcast | null;
  error: any;
  enroll: EnrollPodcastState | null,
}

interface PodcastVoicePresetsState {
  loading: boolean;
  items: VoicePreset[];
  error: any;
}

const initialDraftPodcastItemsState: DraftPodcastItemsState = {
  loading: false,
  items: [],
  error: null,
  pagination: {
    currentPage: -1,
    totalPages: 0,
    recordsPerPage: 10,
    totalRecordsCount: 0,
    hasMoreItems: false,
  },
};

const initialPublishedPodcastItemsState: PublishedPodcastItemsState = {
  loading: false,
  items: [],
  error: null,
  pagination: {
    currentPage: -1,
    totalPages: 0,
    recordsPerPage: 10,
    totalRecordsCount: 0,
    hasMoreItems: false,
  },
};

const initialPodcastCreationState: PodcastCreationState = {
  creating: false,
  created: false,
  item: null,
  error: null,
};

const initialPodcastUpdateState: PodcastUpdateState = {
  id: "",
  updating: false,
  updated: false,
  item: null,
  error: null,
};

const initialPodcastPublishState: PodcastPublishState = {
  id: "",
  publishing: false,
  published: false,
  item: null,
  error: null,
};

const initialPodcastDeletionState: PodcastDeletionState = {
  id: "",
  deleting: false,
  deleted: false,
  error: null,
};

const initialState: PodcastsState = {
  drafts: initialDraftPodcastItemsState,
  published: initialPublishedPodcastItemsState,
  loading: false,
  items: [],
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    recordsPerPage: 10,
    totalRecordsCount: 0,
    hasMoreItems: false,
  },
  creation: initialPodcastCreationState,
  edit: null,
  update: {},
  publish: null,
  deletion: {},
  podcast: {},
  voicePresets: {}
};

export function podcastsReducer(
  state: PodcastsState = initialState,
  action: UnknownAction,
): PodcastsState {
  switch (action.type) {
    case ADD_ASSET_TO_PODCAST: {
      const { id, asset } = action.payload as AddAssetToPodcastRequest;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== id) {
        return state;
      }

      const existingAssets = _.get(editState, "item.assets") || [];
      const assets = [...existingAssets, asset];

      return {
        ...state,
        edit: {
          ...editState,
          item: {
            ...editState.item,
            assets: assets,
          },
        },
      };
    }

    case CREATE_PODCAST_REQUEST: {
      return {
        ...state,
        creation: {
          ...initialPodcastCreationState,
          creating: true,
        },
      };
    }

    case CREATING_PODCAST_SUCCEEDED: {
      const { response } = action.payload as CreatePodcastResponse;
      const creationState = state.creation;
      if (!creationState) {
        return state;
      }

      return {
        ...state,
        creation: {
          ...creationState,
          creating: false,
          created: true,
          item: response,
          error: null,
        },
      };
    }

    case CREATING_PODCAST_FAILED: {
      const { error } = action.payload as CreatePodcastFailed;
      const creationState = state.creation;
      if (!creationState) {
        return state;
      }

      return {
        ...state,
        creation: {
          ...creationState,
          creating: false,
          created: true,
          error: error,
        },
      };
    }

    case INVALIDATE_PODCAST_CREATION_STATE: {
      return {
        ...state,
        creation: null,
      };
    }

    case FETCH_DRAFT_PODCAST_REQUEST: {
      const id = action.payload as string;

      const podcastState = state.podcast[id] || {
        loading: false,
        loaded: false,
        item: null,
        error: null,
      };

      return {
        ...state,
        podcast: {
          ...state.podcast,
          [id]: {
            ...podcastState,
            loading: true,
            loaded: false,
            item: null,
            error: null,
          },
        },
      };
    }

    case FETCHING_DRAFT_PODCAST_SUCCEEDED: {
      const { response } = action.payload as { response: Podcast };
      const id = response.id || "";

      if (!response || !id) {
        return state;
      }

      const podcastState = state.podcast[id];
      if (!podcastState) {
        return state;
      }

      return {
        ...state,
        podcast: {
          ...state.podcast,
          [id]: {
            ...podcastState,
            loading: false,
            loaded: true,
            item: response,
            error: null,
          },
        },
      };
    }

    case FETCHING_DRAFT_PODCAST_FAILED: {
      const { id, error } = action.payload as { id: string; error: any };

      const podcastState = state.podcast[id];
      if (!podcastState) {
        return state;
      }

      return {
        ...state,
        podcast: {
          ...state.podcast,
          [id]: {
            ...podcastState,
            loading: false,
            loaded: false,
            item: null,
            error: error,
          },
        },
      };
    }

    case INVALIDATE_DRAFT_PODCAST: {
      const id = action.payload as string;
      let podcastState = state.podcast;
      if (podcastState[id]) {
        podcastState = _.omit(podcastState, id);
      }

      let editState = state.edit;
      if (editState && editState.item?.id === id) {
        editState = null;
      }

      let updateState = state.update;
      if (updateState[id]) {
        updateState = _.omit(updateState, id);
      }

      return {
        ...state,
        podcast: {
          ...podcastState,
        },
        update: {
          ...updateState,
        },
        edit: editState,
      };
    }

    case EDIT_PODCAST_REQUEST: {
      const podcast = action.payload as Podcast;

      return {
        ...state,
        edit: {
          item: podcast,
          editorSession: null,
          errors: [],
          assets: {},
        },
      };
    }

    case UPDATE_PODCAST_PROPERTY_REQUEST: {
      const { podcast, property, value } =
        action.payload as UpdatePodcastPropertyRequest;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== podcast) {
        return state;
      }

      let updatedItem = _.cloneDeep(editState.item);
      updatedItem = _.set(updatedItem, property, value);
      const errors = validatePodcastRequest(updatedItem);

      return {
        ...state,
        edit: {
          ...editState,
          item: updatedItem,
          errors: errors,
        },
      };
    }

    case UPDATE_VALIDATION_ERRORS_FOR_PODCAST: {
      const { podcast, errors } = action.payload as UpdateValidationErrorsForPodcastRequest;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== podcast) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          errors: errors,
        },
      };
    }

    case DISCARD_PODCAST_DRAFT_REQUEST: {
      const id = action.payload as string;
      let updateState = state.update[id] || initialPodcastUpdateState;

      updateState = {
        ...updateState,
        id,
        updating: true,
        updated: false,
        item: null,
        error: null,
      };

      return {
        ...state,
        update: {
          ...state.update,
          [id]: updateState,
        },
      };
    }

    case UPDATE_PODCAST_REQUEST: {
      const { id } = action.payload as UpdatePodcastRequest;

      const updateState = state.update[id] || initialPodcastUpdateState;

      return {
        ...state,
        update: {
          ...state.update,
          [id]: {
            ...updateState,
            updating: true,
            updated: false,
            item: null,
            error: null,
          },
        },
      };
    }

    case UPDATING_PODCAST_SUCCEEDED:
    case DISCARDING_PODCAST_DRAFT_SUCCEEDED: {
      const { response } = action.payload as UpdatePodcastResponse;

      const id = response.id;
      if (!id) {
        return state;
      }

      let update = state.update;

      const podcastState = state.podcast[id];

      const items = state.items.map((item) => {
        if (item.id === id) {
          return response;
        }

        return item;
      });

      const updateState = state.update[id];
      if (updateState) {
        update = {
          ...update,
          [id]: {
            ...updateState,
            updating: false,
            updated: true,
            item: response,
            error: null,
          },
        }
      }

      let editState = state.edit as any;
      if (editState) {
        editState = {
          ...editState,
          item: {
            ...editState.item,
            ...response,
          },
        };

        if (_.has(editState, "editorSession.transcript.hasChanges")) {
          editState = {
            ...editState,
            editorSession: {
              ...editState.editorSession,
              transcript: {
                ...editState.editorSession!.transcript,
                hasChanges: false,
              },
            },
          }
        }
      }

      return {
        ...state,
        items,
        update: update,
        podcast: {
          ...state.podcast,
          [id]: {
            ...podcastState,
            item: response,
          },
        },
        edit: editState
      };
    }

    case UPDATING_PODCAST_FAILED:
    case DISCARDING_PODCAST_DRAFT_FAILED: {
      const { id, error } = action.payload as UpdatePodcastErrorResponse;
      const updateState = state.update[id];
      if (!updateState) {
        return state;
      }

      return {
        ...state,
        update: {
          ...state.update,
          [id]: {
            ...updateState,
            updating: false,
            updated: false,
            error: error,
          },
        },
      };
    }

    case INVALIDATE_PODCAST_UPDATE_STATE: {
      return {
        ...state,
        update: {},
      };
    }

    case PUBLISH_PODCAST_REQUEST: {
      const id = action.payload as string;
      return {
        ...state,
        publish: {
          ...initialPodcastPublishState,
          id,
          publishing: true,
        },
      };
    }

    case PUBLISHING_PODCAST_SUCCEEDED: {
      const { response } = action.payload as PublishPodcastResponse;
      const publishState = state.publish;
      if (!publishState) {
        return state;
      }

      if (publishState.id !== response.id) {
        return state;
      }

      let editState = state.edit;
      if (editState) {
        const { status, hasChanges, lastPublished } = response;
        editState = {
          ...editState,
          item: {
            ...editState.item,
            status,
            hasChanges,
            lastPublished,
          },
        };
      }

      return {
        ...state,
        publish: {
          ...publishState,
          publishing: false,
          published: true,
          item: response,
          error: null,
        },
        edit: editState,
      };
    }

    case PUBLISHING_PODCAST_FAILED: {
      const { id, error } = action.payload as PublishPodcastErrorResponse;
      const publishState = state.publish;
      if (!publishState) {
        return state;
      }

      if (publishState.id !== id) {
        return state;
      }

      return {
        ...state,
        publish: {
          ...publishState,
          publishing: false,
          published: false,
          error: error,
        },
      };
    }

    case INVALIDATE_PUBLISH_PODCAST: {
      return {
        ...state,
        publish: null,
      };
    }

    case DELETE_PODCAST_REQUEST: {
      const id = action.payload as string;

      let deletionState = state.deletion[id] || initialPodcastDeletionState;

      deletionState = {
        ...deletionState,
        id,
        deleting: true,
        deleted: false,
        error: null,
      };

      return {
        ...state,
        deletion: {
          ...state.deletion,
          [id]: deletionState,
        },
      };
    }

    case DELETING_PODCAST_SUCCEEDED: {
      const { id } = action.payload as DeletePodcastResponse;
      const deletionState = state.deletion[id];
      if (!deletionState) {
        return state;
      }

      const items = state.published.items?.filter((item) => {
        return item.id !== id;
      });

      const drafts = state.drafts.items?.filter((item) => {
        return item.id !== id;
      });

      return {
        ...state,
        published: {
          ...state.published,
          items: items,
        },
        drafts: {
          ...state.drafts,
          items: drafts,
        },
        deletion: {
          ...state.deletion,
          [id]: {
            ...deletionState,
            deleting: false,
            deleted: true,
            error: null,
          },
        },
      };
    }

    case DELETING_PODCAST_FAILED: {
      const { id, error } = action.payload as DeletePodcastErrorResponse;
      const deletionState = state.deletion[id];
      if (!deletionState) {
        return state;
      }

      return {
        ...state,
        deletion: {
          ...state.deletion,
          [id]: {
            ...deletionState,
            deleting: false,
            deleted: false,
            error: error,
          },
        },
      };
    }

    //Podcast Editor
    case INITIATE_PODCAST_EDITOR_REQUEST: {
      const podcast = action.payload as string;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== podcast) {
        return state;
      }

      const transcript = _.get(state, 'edit.item.transcription.item') || null;

      return {
        ...state,
        edit: {
          ...editState,
          editorSession: {
            session: null,
            podcast: podcast,
            chats: [],
            transcript: {
              loading: false,
              loaded: false,
              hasChanges: false,
              item: transcript,
              error: null,
            }
          },
        },
      };
    }

    case INVALIDATE_PODCAST_EDITOR_REQUEST: {
      const podcast = action.payload as string;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== podcast) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          editorSession: null,
        },
      }
    }

    case CREATING_PODCAST_EDITOR_CHAT_REQUEST: {
      const { podcast, session, input } = action.payload as CreatePodcastEditorChatRequest;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== podcast) {
        return state;
      }

      let sessionState = editState.editorSession || {
        session: null,
        chats: [],
        transcript: {
          loading: false,
          loaded: false,
          hasChanges: false,
          item: null,
          error: null,
        }
      };

      sessionState = {
        ...sessionState,
        session: session,
        podcast: podcast,
        chats: [
          ...(sessionState.chats || []),
          {
            input: input,
            output: "",
            creating: true,
            created: false,
            error: null,
          },
        ],
      }

      return {
        ...state,
        edit: {
          ...editState,
          editorSession: sessionState,
        },
      };
    }

    case CREATING_PODCAST_EDITOR_CHAT_SUCCEEDED: {
      const response = action.payload as CreatePodcastEditorChatResponse;
      const editState = state.edit;

      if (!editState) {
        return state;
      }

      if (editState.item.id !== response.podcast) {
        return state;
      }

      const sessionState = editState.editorSession;
      if (!sessionState) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          editorSession: {
            ...sessionState,
            session: response.session,
            chats: sessionState.chats.map((c) => {
              if (c.creating && c.input === response.input) {
                return {
                  ...c,
                  ...response,
                  creating: false,
                  created: true,
                  error: null,
                };
              }

              return c;
            })
          },
        },
      };
    }

    case CREATING_PODCAST_EDITOR_CHAT_FAILED: {
      const { podcast, error } = action.payload as CreatePodcastEditorChatErrorResponse;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== podcast) {
        return state;
      }

      const sessionState = editState.editorSession;
      if (!sessionState) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          editorSession: {
            ...sessionState,
            chats: sessionState.chats.map((c) => {
              if (c.creating) {
                return {
                  ...c,
                  output: "",
                  creating: false,
                  created: false,
                  error: error,
                };
              }

              return c;
            })
          },
        },
      };
    }

    case WS_PODCAST_EDIT_TRANSCRIPT_COMPLETED: {
      const response = action.payload as CreatePodcastEditorChatResponse;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== response.podcast) {
        return state;
      }

      const sessionState = editState.editorSession;
      if (sessionState?.session !== response.session) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          editorSession: {
            ...sessionState,
            chats: sessionState.chats.map((c) => {
              if (c.id === response.id) {
                return {
                  ...c,
                  ...response,
                };
              }

              return c;
            })
          },
        },
      };
    }

    case FETCH_PODCAST_TRANSCRIPTION_FOR_EDIT_CHAT_REQUEST: {
      const { podcast, session } = action.payload as FetchPodcastTranscriptionForEditChat;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== podcast) {
        return state;
      }

      const sessionState = editState.editorSession;
      if (sessionState?.session !== session) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          editorSession: {
            ...sessionState,
            transcript: {
              ...sessionState.transcript,
              loading: true,
              loaded: false,
              error: null,
            },
          },
        },
      };
    }

    case FETCHING_PODCAST_TRANSCRIPTION_FOR_EDIT_CHAT_SUCCEEDED: {
      const { podcast, session, response } = action.payload as FetchPodcastTranscriptionForEditChatResponse;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== podcast) {
        return state;
      }

      const sessionState = editState.editorSession;
      if (sessionState?.session !== session) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          editorSession: {
            ...sessionState,
            transcript: {
              ...sessionState.transcript,
              loading: false,
              loaded: true,
              hasChanges: true,
              item: response,
              error: null,
            },
          },
        },
      };
    }

    case FETCHING_PODCAST_TRANSCRIPTION_FOR_EDIT_CHAT_FAILED: {
      const { podcast, session, error } = action.payload as FetchPodcastTranscriptionForEditChatErrorResponse;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== podcast) {
        return state;
      }

      const sessionState = editState.editorSession;
      if (sessionState?.session !== session) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          editorSession: {
            ...sessionState,
            transcript: {
              ...sessionState.transcript,
              loading: false,
              loaded: false,
              error: error,
            },
          },
        },
      };
    }

    //Fetch podcast transcription for draft
    case FETCH_PODCAST_TRANSCRIPTION_REQUEST: {
      const id = action.payload as string;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== id) {
        return state;
      }

      let editorSession = editState.editorSession;
      if (editorSession) {
        editorSession = {
          ...editorSession,
          transcript: {
            ...editorSession.transcript,
            loading: true,
            loaded: false,
            error: null,
          }
        }
      }

      return {
        ...state,
        edit: {
          ...editState,
          item: {
            ...editState.item,
            transcription: {
              loading: true,
              loaded: false,
              item: null,
              error: null,
            },
          },
          editorSession: editorSession,
        },
      };
    }

    case FETCHING_PODCAST_TTRANSCRIPTION_SUCCEEDED: {
      const { id, response } = action.payload as { id: string; response: any };

      if (!response || !id) {
        return state;
      }

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== id) {
        return state;
      }

      let editorSession = editState.editorSession;
      if (editorSession) {
        editorSession = {
          ...editorSession,
          transcript: {
            ...editorSession.transcript,
            loading: false,
            loaded: false,
            item: response,
            error: null,
          }
        }
      }

      return {
        ...state,
        edit: {
          ...editState,
          item: {
            ...editState.item,
            transcription: {
              loading: false,
              loaded: true,
              item: response,
              error: null,
            },
          },
          editorSession: editorSession,
        },
      };
    }

    case FETCHING_PODCAST_TRANSCRIPTION_FAILED: {
      const { id, error } = action.payload as { id: string; error: any };

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== id) {
        return state;
      }

      let editorSession = editState.editorSession;
      if (editorSession) {
        editorSession = {
          ...editorSession,
          transcript: {
            ...editorSession.transcript,
            loading: false,
            loaded: false,
            error: error,
          }
        }
      }

      return {
        ...state,
        edit: {
          ...editState,
          item: {
            ...editState.item,
            transcription: {
              loading: false,
              loaded: false,
              item: null,
              error: error,
            },
          },
        },
      };
    }

    case QUICK_GENERATE_PODCAST_REQUEST: {
      const { id } = action.payload as QuickGeneratePodcastRequest;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== id) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          quickGenerate: {
            generating: true,
            generated: false,
            error: null,
          },
          generateAudio: null,
          generateTranscript: null,
        },
      };
    }

    case QUICK_GENERATING_PODCAST_SUCCEEDED: {
      const { id } = action.payload as any;

      if (!id) {
        return state;
      }

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== id) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          quickGenerate: {
            generating: false,
            generated: true,
            error: null,
          },
        },
      };
    }

    case QUICK_GENERATING_PODCAST_FAILED: {
      const { id, error } = action.payload as any;

      if (!id) {
        return state;
      }

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== id) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          quickGenerate: {
            generating: false,
            generated: false,
            error: error,
          },
        },
      };
    }

    case INVALIDATE_QUICK_GENERATING_PODCAST_STATE: {
      const id = action.payload as string;

      if (!id) {
        return state;
      }

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== id) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          quickGenerate: null,
        },
      };
    }

    case GENERATE_TRANSCRIPT_FOR_PODCAST_REQUEST: {
      const { id } = action.payload as GenerateTranscriptForPodcastRequest;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== id) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          generateTranscript: {
            generating: true,
            generated: false,
            error: null,
          },
          generateAudio: null,
        },
      };
    }

    case GENERATING_TRANSCRIPT_FOR_PODCAST_SUCCEEDED: {
      const { id, response } = action.payload as any;

      if (!id) {
        return state;
      }

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== id) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          item: {
            ...editState.item,
            transcriptProcessing: response.transcriptProcessing,
          }
        },
      };
    }

    case GENERATING_TRANSCRIPT_FOR_PODCAST_FAILED: {
      const { id, error } = action.payload as {
        id: string;
        error: any;
      };

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== id) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          generateTranscript: {
            generating: false,
            generated: false,
            error: error,
          },
        },
      };
    }

    //Audio
    case GENERATE_AUDIO_FOR_PODCAST_REQUEST: {
      const { id } = action.payload as GenerateAudioForPodcastRequest;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== id) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          generateAudio: {
            generating: true,
            generated: false,
            error: null,
          },
          generateTranscript: null,
        },
      };
    }

    case GENERATING_AUDIO_FOR_PODCAST_SUCCEEDED: {
      const { id, response } = action.payload as any;

      if (!id) {
        return state;
      }

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== id) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          item: {
            ...editState.item,
            audioProcessing: response.audioProcessing,
          }
        },
      };
    }

    case GENERATING_AUDIO_FOR_PODCAST_FAILED: {
      const { id, error } = action.payload as {
        id: string;
        error: any;
      };

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== id) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          generateAudio: {
            generating: false,
            generated: false,
            error: error,
          },
        },
      };
    }

    case WS_PODCAST_TOPICS_GENERATION_COMPLETED: {
      const { podcast, topics } = action.payload as PodcastTopicsWebsocketResponse;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== podcast) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          item: {
            ...editState.item,
            topics: topics,
          }
        },
      }
    }

    case WS_PODCAST_TRANSCRIPT_GENERATION_COMPLETED: {
      const { podcast } = action.payload as PodcastTranscriptionWebsocketResponse;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== podcast) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          generateTranscript: {
            generating: false,
            generated: true,
            error: null
          },
        },
      }
    }

    case WS_PODCAST_AUDIO_GENERATION_COMPLETED: {
      const { podcast } = action.payload as PodcastAudioWebsocketResponse;

      const editState = state.edit;
      if (!editState) {
        return state;
      }

      if (editState.item.id !== podcast) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...editState,
          generateAudio: {
            generating: false,
            generated: true,
            error: null
          },
        },
      }
    }

    case SYNCING_PODCAST_PROCESSING_STATE_SUCCEEDED: {
      const { response } = action.payload as FetchPodcastResponse;

      const id = response.id || "";

      if (!response || !id) {
        return state;
      }

      const podcastState = state.podcast[id];
      if (!podcastState) {
        return state;
      }

      let editState = state.edit;
      if (editState && editState.item.id === id) {
        let generateAudioState = editState.generateAudio;
        if (response.audioProcessing?.status === "in_progress") {
          generateAudioState = {
            generating: true,
            generated: false,
            error: null,
          };
        }

        editState = {
          ...editState,
          item: {
            ...editState.item,
            type: response.type,
            assets: response.assets,
            duration: response.duration,
            voicePreset: response.voicePreset,
            transcriptUrl: response.transcriptUrl,
            topics: response.topics,
            audio: response.audio,
            transcriptProcessing: response.transcriptProcessing,
            audioProcessing: response.audioProcessing,
          },
          generateAudio: generateAudioState
        };
      }

      return {
        ...state,
        edit: editState,
        podcast: {
          ...state.podcast,
          [id]: {
            ...podcastState,
            item: {
              ...podcastState.item,
              type: response.type,
              assets: response.assets,
              duration: response.duration,
              voicePreset: response.voicePreset,
              transcriptUrl: response.transcriptUrl,
              topics: response.topics,
              audio: response.audio,
              transcriptProcessing: response.transcriptProcessing,
              audioProcessing: response.audioProcessing,
            }
          },
        },
      };
    }

    case FETCH_PUBLIC_PODCAST_REQUEST: {
      const { id } = action.payload as FetchPublicPodcastRequest;
      const podcastState = state.podcast[id] || {
        loading: false,
        loaded: false,
        item: null,
        error: null
      };

      return {
        ...state,
        podcast: {
          ...state.podcast,
          [id]: {
            ...podcastState,
            loading: true,
            loaded: false,
            error: null
          }
        }
      }
    }

    case FETCHING_PUBLIC_PODCAST_SUCCEEDED: {
      const { podcast, learningRecord } = action.payload as FetchPublicPodcastResponse;

      if (!podcast || !podcast.id) {
        return state;
      }

      const id = podcast.id;

      const podcastState = state.podcast[podcast.id];
      if (!podcastState) {
        return podcastState;
      }

      if (learningRecord) {
        return {
          ...state,
          podcast: {
            ...state.podcast,
            [id]: {
              ...podcastState,
              loading: false,
              loaded: true,
              item: podcast,
              error: null,
              enroll: {
                ...podcastState.enroll,
                enrolling: false,
                enrolled: true,
                id: learningRecord.id,
                parentCourse: learningRecord.parentCourse,
                error: null
              },
            }
          }
        }
      }

      return {
        ...state,
        podcast: {
          ...state.podcast,
          [podcast.id]: {
            ...podcastState,
            loading: false,
            loaded: true,
            item: podcast,
            error: null,
          }
        }
      }
    }

    case FETCHING_PUBLIC_PODCAST_FAILED: {
      const { id, error } = action.payload as FetchPublicPodcastErrorResponse;
      const podcastState = state.podcast[id];
      if (!podcastState) {
        return podcastState;
      }

      return {
        ...state,
        podcast: {
          ...state.podcast,
          [id]: {
            ...podcastState,
            loading: false,
            loaded: false,
            item: null,
            error: error,
          }
        }
      }
    }
    case ENROLL_PODCAST_REQUEST: {
      const id = action.payload as string;
      const podcastState = state.podcast[id];

      if (!podcastState) {
        return state;
      }

      return {
        ...state,
        podcast: {
          ...state.podcast,
          [id]: {
            ...podcastState,
            enroll: {
              enrolling: true,
              enrolled: false,
              error: null
            }
          }
        }
      }
    }

    case ENROLLING_PODCAST_SUCCEEDED: {
      const { id, response } = action.payload as EnrollCourseResponse;
      const podcastState = state.podcast[id];

      if (!podcastState) {
        return state;
      }

      const enrollId = response.learningRecord.id;

      return {
        ...state,
        podcast: {
          ...state.podcast,
          [id]: {
            ...podcastState,
            enroll: {
              id: enrollId,
              enrolling: false,
              enrolled: true,
              error: null
            }
          }
        }
      }
    }

    case ENROLLING_PODCAST_FAILED: {
      const { id, error } = action.payload as EnrollCourseErrorResponse;

      const podcastState = state.podcast[id];
      if (!podcastState) {
        return state;
      }

      return {
        ...state,
        podcast: {
          ...state.podcast,
          [id]: {
            ...podcastState,
            enroll: {
              enrolling: false,
              enrolled: false,
              error: error
            },
          }
        }
      }
    }

    case FETCH_PODCAST_VOICE_PRESETS_REQUEST: {
      const podcastType = action.payload as string;

      const presetsState = state.voicePresets[podcastType] || {
        loading: true,
        items: [],
        error: null,
      };

      return {
        ...state,
        voicePresets: {
          ...state.voicePresets,
          [podcastType]: {
            ...presetsState,
            loading: true,
            error: null,
          },
        },
      };
    }

    case FETCHING_PODCAST_VOICE_PRESETS_SUCCEEDED: {
      const { type, response } = action.payload as FetchVoicePresetResponse;

      const presetsState = state.voicePresets[type] || {
        loading: false,
        items: [],
        error: null,
      };

      return {
        ...state,
        voicePresets: {
          ...state.voicePresets,
          [type]: {
            ...presetsState,
            loading: false,
            items: response,
            error: null,
          }
        },
      };
    }

    case FETCHING_PODCAST_VOICE_PRESETS_FAILED: {
      const { type, error } = action.payload as FetchVoicePresetErrorResponse;

      return {
        ...state,
        voicePresets: {
          ...state.voicePresets,
          [type]: {
            loading: false,
            items: [],
            error: error,
          },
        },
      };
    }

    default: {
      return state;
    }
  }
}
